import { StepProps } from '@betterment/js-runtime';
import {
  Button,
  CenteredLayout,
  Heading,
  IconLink,
  Link,
  Logo,
  PancakeStack,
  Separator,
  Text,
  WorkflowHeader,
} from '@betterment/samba-react';
import { Form, Formik } from 'formik';
import { date, object } from 'yup';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { BenefitReportCreationOption } from '../../data';
import { RunReportOptions } from '../../Options';

type Props = {
  employerId: string;
} & StepProps;

export const GenerateReportsStep = ({ employerId, goToNextStep }: Props) => {
  const initialValues: BenefitReportCreationOption = {
    type: '',
    start_date: undefined,
    end_date: undefined,
    as_of_date: undefined,
    show_ssn: false,
    show_only_most_recent_changes: false,
    show_only_active_employees: false,
  };

  const onSubmit = () => goToNextStep();

  const ReportCreationValidationSchema = object().shape({
    start_date: date().typeError('is not a valid date').optional(),
    end_date: date().typeError('is not a valid date').optional(),
    as_of_date: date().typeError('is not a valid date').optional(),
  });

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={ReportCreationValidationSchema}>
      {({ isSubmitting, values }) => (
        <Form id="run-report" style={{ width: '100%' }}>
          <PancakeStack css={{ maxWidth: '100%', height: '100%', position: 'relative' }}>
            <PancakeStack.Header
              css={{
                maxWidth: '100%',
                display: 'flex',
                justifyContent: 'center',
                borderBottom: 'solid thin',
                borderColor: 'border',
              }}
            >
              <WorkflowHeader>
                <WorkflowHeader.Leading>
                  <Link href={urlBuilder.dashboard()} aria-label="Back to dashboard">
                    <Logo type="b4bSignature" />
                  </Link>
                </WorkflowHeader.Leading>
                <WorkflowHeader.Title>Run report</WorkflowHeader.Title>
                <WorkflowHeader.Trailing>
                  <IconLink name="X" label="Close" spacing="none" href={urlBuilder.reports()} />
                </WorkflowHeader.Trailing>
              </WorkflowHeader>
            </PancakeStack.Header>
            <PancakeStack.Main>
              <CenteredLayout size="medium" css={{ py: 'space.600' }}>
                <Heading>Run a report.</Heading>
                <Text as="p" css={{ my: 'space.300' }}>
                  Please select the report you’d like to run, and then specify the date(s) for your report. Your report
                  will be generated as a CSV file.
                </Text>
                <ErrorBoundary name="runReportOptions" fallbackType="full">
                  <RunReportOptions employerId={employerId} />
                </ErrorBoundary>
              </CenteredLayout>
            </PancakeStack.Main>
            <PancakeStack.Footer
              css={{ position: 'sticky', bottom: '0', width: '100%', background: 'background.surface' }}
            >
              <Separator />
              <CenteredLayout size="medium" css={{ p: 'space.200' }}>
                <Button disabled={isSubmitting || !values.type} type="submit">
                  Submit
                </Button>
              </CenteredLayout>
            </PancakeStack.Footer>
          </PancakeStack>
        </Form>
      )}
    </Formik>
  );
};
